import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveApp,
  type AppSummary,
  getAppsOverview,
  rollbackApp,
  unpublishApp,
} from "../../api/hub/apps";
import { getMe, listPeople } from "../../api/hub/identity";
import { VersionPanel } from "./VersionPanel";
import { AppMetadata, AppScope } from "./AppMetadata";
import "./apps.css";
export function AdminAppsOverview() {
  const me = useQuery({ queryKey: ["hub", "me"], queryFn: getMe }),
    allowed = !!me.data && !me.data.isAI &&
      ["CEO", "CTO"].includes(me.data.role);
  const query = useQuery({
      queryKey: ["hub", "apps", "overview"],
      queryFn: getAppsOverview,
      enabled: allowed,
      refetchInterval: 5000,
    }),
    people = useQuery({
      queryKey: ["hub", "people"],
      queryFn: listPeople,
      enabled: allowed,
    }),
    qc = useQueryClient(),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [selected, setSelected] = useState<AppSummary | null>(null);
  const run = async (action: () => Promise<AppSummary>, success: string) => {
    setBusy(true);
    setError("");
    try {
      await action();
      setMessage(success);
      await qc.invalidateQueries({ queryKey: ["hub", "apps"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败，请重试");
    } finally {
      setBusy(false);
    }
  };
  if (me.isPending) return <p>正在核对管理员身份…</p>;
  if (!allowed) return <p>应用管理总览仅对 CEO、CTO 开放。</p>;
  return (
    <section className="apps-admin" aria-label="应用管理总览">
      <header className="apps-section-heading">
        <div>
          <p className="apps-eyebrow">管理总览</p>
          <h2>应用如何被使用</h2>
        </div>
        <button
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          刷新总览
        </button>
      </header>
      <p className="apps-meta">
        仅展示调用者、数据工具、次数与失败数，不展示读取的数据正文。共享审批精确绑定候选版本和权限范围。
      </p>
      {(error || query.error) && (
        <p role="alert">{error || query.error?.message}。请重试。</p>
      )}
      {message && <p role="status">{message}</p>}
      {query.isPending ? <p>正在加载总览…</p> : (
        <>
          <div className="apps-admin-pending">
            {query.data?.apps.filter((a) => a.approval?.status === "pending")
              .map((app) => (
                <article key={app.id}>
                  <h3>{app.name} · 候选 v{app.approval?.version}</h3>
                  <p>
                    作者 {app.ownerName} · 内容 {app.contentHash.slice(0, 12)}
                  </p>
                  <div className="apps-detail-metadata">
                    <AppMetadata app={app} />
                    <AppScope app={app} />
                  </div>
                  {me.data?.role === "CEO"
                    ? (
                      <div className="apps-actions">
                        <button
                          className="apps-primary"
                          disabled={busy || app.status === "disabled"}
                          onClick={() => {
                            if (
                              window.confirm(
                                `批准「${app.name}」第 ${app.approval?.version} 版共享给全员？\n工具：${
                                  app.manifest.tools.join("、") || "无"
                                }\n外联：${
                                  [
                                    ...app.manifest.csp.connectDomains,
                                    ...app.manifest.csp.resourceDomains,
                                  ].join("、") || "无"
                                }`,
                              )
                            ) {
                              void run(
                                () => approveApp(app, true),
                                "此精确版本已批准共享",
                              );
                            }
                          }}
                        >
                          批准候选版本
                        </button>
                        <button
                          disabled={busy}
                          onClick={() =>
                            void run(
                              () => approveApp(app, false),
                              "共享申请已退回",
                            )}
                        >
                          退回申请
                        </button>
                      </div>
                    )
                    : <p>等待 CEO 本人在网页或飞书审批。</p>}
                </article>
              ))}
          </div>
          <div className="apps-table-scroll">
            <table>
              <caption>数据调用记录</caption>
              <thead>
                <tr>
                  <th>应用</th>
                  <th>使用者</th>
                  <th>读取的工具</th>
                  <th>调用次数</th>
                  <th>失败</th>
                  <th>返回字节</th>
                </tr>
              </thead>
              <tbody>
                {query.data?.usage.map((u, i) => (
                  <tr key={i}>
                    <td>
                      {query.data.apps.find((a) => a.id === u.appId)?.name ??
                        u.appId}
                    </td>
                    <td>
                      {u.personName ??
                        people.data?.find((p) => p.id === u.personId)?.name ??
                        u.personId}
                    </td>
                    <td>{u.tool}</td>
                    <td>{u.calls}</td>
                    <td>{u.errors}</td>
                    <td>{u.bytes.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!query.data?.usage.length && <p>尚无应用工具调用。</p>}
          </div>
          <details>
            <summary>全部应用与停用</summary>
            {query.data?.apps.map((a) => (
              <div className="apps-admin-row" key={a.id}>
                <strong>{a.name}</strong>
                <span>{a.ownerName}</span>
                <AppMetadata app={a} />
                <button
                  onClick={() =>
                    setSelected(a)}
                >
                  查看版本与回滚
                </button>
                <button
                  disabled={busy || a.status === "disabled"}
                  onClick={() => {
                    if (
                      window.confirm(
                        `停用「${a.name}」？现有沙箱连接也将关闭。`,
                      )
                    ) {
                      void run(() =>
                        unpublishApp(a.id), "应用已停用");
                    }
                  }}
                >
                  停用应用
                </button>
              </div>
            ))}
          </details>
          <details>
            <summary>通知投递状态</summary>
            {query.data?.notifications.map((n) => (
              <p key={n.id}>
                {query.data.apps.find((a) => a.id === n.appId)?.name ?? n.appId}
                {" "}
                · {n.sentAt ? "已进入通知服务" : `待重试（${n.attempts} 次）`}
                {n.lastError ? ` · ${n.lastError}` : ""}
              </p>
            ))}
            {!query.data?.notifications.length && <p>暂无通知记录。</p>}
          </details>
        </>
      )}
      {selected && (
        <VersionPanel
          app={query.data?.apps.find((a) => a.id === selected.id) ?? selected}
          busy={busy}
          onClose={() => setSelected(null)}
          onRollback={(version) => {
            if (
              window.confirm(
                `恢复「${selected.name}」到第 ${version} 版及其批准范围？`,
              )
            ) {
              void run(
                () => rollbackApp(selected.id, version),
                "已恢复所选版本",
              );
            }
          }}
        />
      )}
    </section>
  );
}
